import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomFieldType, RenderSeparator, renderSeparatorString } from '@ee/common/enums';
import { ANY_SNAKE_CASE, CUSTOM_FIELD_CODE } from '@ee/common/constants';
import { AvailableFieldValidator, MULTI_VALUE_DELIMITER, ValidateQuillEquation } from '@ee/common/validators';
import { CustomField, CustomFieldOption } from '@ee/common/models';
import { CUSTOM_FIELDS } from './custom-fields.constants';

export type CustomFieldForm = FormGroup<{
  order: FormControl<number>;
  columns: FormControl<number>;
  label: FormControl<string>;
  hint: FormControl<string>;
  hint_more_info: FormControl<string>;
  field: FormControl<string>;
  type: FormControl<CustomFieldType>;
  required: FormControl<boolean>;
  prevent_removal: FormControl<boolean>;
  link_id: FormControl<string>;
  false_label: FormControl<string>;
  true_label: FormControl<string>;
  include_in_total: FormControl<boolean>;
  monetary: FormControl<boolean>;
  hide: FormControl<boolean>;
  hide_from_intake: FormControl<boolean>;
  value: FormControl<any>;
  options: FormControl<string[]>;
  /**
   * MULTI_SELECT only — one {label, value} row per checkbox. Present on the builder form (which
   * edits a field definition) and absent from the render form, hence optional.
   */
  option_definitions?: FormArray<CustomFieldOptionForm>;
  /** MULTI_SELECT only — how selections join in documents/reports. Null for every other type. */
  render_separator?: FormControl<RenderSeparator | null>;
  built_in_field: FormControl<boolean>;
  description: FormControl<string>;
  example: FormControl<string>;
}>;
export type CustomFieldOptionForm = FormGroup<{
  label: FormControl<string>;
  value: FormControl<string>;
}>;

/** One editable {label, value} option row of a MULTI_SELECT field. */
export function GenerateCustomFieldOptionForm(fb: FormBuilder, option?: CustomFieldOption): CustomFieldOptionForm {
  return fb.group({
    label: [option?.label ?? ''],
    value: [option?.value ?? '']
  });
}

/**
 * The option rows a MULTI_SELECT field opens with: its definitions, or — for a field that only ever
 * had bare `options` (a legacy SurveyJS checkbox question) — one row per option where the label
 * equals the value (Req 2.2).
 */
function multiSelectSeedOptions(values: CustomField | undefined): CustomFieldOption[] {
  return values?.option_definitions?.length
    ? values.option_definitions
    : (values?.options ?? []).map(value => ({value, label: value}));
}

/**
 * Fills every blank stored value in from its label (Req 4.6), across a MULTI_SELECT field's option
 * rows, and keeps `options` equal to the ordered list of the resulting values (Req 2.3).
 *
 * Called on load and again on save rather than only from the editor's blur handler: a row that is
 * never blurred — a keyboard submit, or definitions that arrive with a blank value — would otherwise
 * submit `{label: 'Mailed', value: ''}` while `options` already said `['Mailed']`, an inconsistent
 * payload the server's non-blank check rejects.
 */
export function NormalizeMultiSelectOptionValues(form: CustomFieldForm): void {
  const rows = form?.get('option_definitions') as FormArray;
  if (!rows) {
    return;
  }
  rows.controls.forEach(row => {
    const label = (row.get('label')?.value ?? '').trim();
    const value = (row.get('value')?.value ?? '').trim();
    if (!value && label) {
      row.get('value').setValue(label, {emitEvent: false});
    }
  });
  const values = rows.controls
    .map(row => ((row.get('value')?.value ?? '').trim() || (row.get('label')?.value ?? '').trim()))
    .filter(v => v);
  form.get('options')?.setValue(values, {emitEvent: false});
  rows.updateValueAndValidity({emitEvent: false});
}

/**
 * A MULTI_SELECT control always holds a real array while the user is ticking boxes, but the wire
 * shape is a single string: `CustomFieldDTO.value` is a `String?`, and the server splits it back
 * into a list with `CustomFieldUtils.splitMultiValueCell`. Req 2.5 forbids the delimiter inside any
 * option label or value (enforced by `MultiSelectOptionsValidator`), so the join is lossless.
 */
export function ParseMultiSelectValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    // Trimmed on both branches, or the same value round-trips differently depending on which side it
    // arrived from: ' A ' from the wire becomes 'A', while [' A '] from a control would stay ' A '.
    return value.map(v => `${v ?? ''}`.trim()).filter(v => !!v);
  }
  if (typeof value === 'string') {
    // '' is a real answer — "I ticked nothing" — and must parse to [], not ['']
    return value.split(MULTI_VALUE_DELIMITER).map(v => v.trim()).filter(v => !!v);
  }
  return [];
}

/**
 * The wire form of a MULTI_SELECT control's value. Nothing ticked yields `''`, never null: the
 * server drops fields whose value is null (`customFields.filter { it.value != null }`), so a null
 * would silently discard an answer the user actually gave.
 */
export function SerializeMultiSelectValue(value: unknown): string {
  return ParseMultiSelectValue(value).join(MULTI_VALUE_DELIMITER);
}

/**
 * One stored answer as it should READ on screen.
 *
 * A MULTI_SELECT value can reach a display surface still `;`-joined — most importantly a client
 * question's answer, which `answerClientQuestion` writes into `FieldValue.value`, a `String?` by
 * schema, so unlike a case custom field it is never converted to an array server-side. Printed raw
 * it leaks the wire delimiter to the user and ignores the separator the field is configured with
 * (Req 10.5).
 *
 * Shared, because there is more than one such surface — the action-centre step view and the case
 * dates/detail list — and a fix applied to only one of them is how this feature has repeatedly
 * shipped half-done. Every other field type is already a display string and passes straight through,
 * so this is safe to call unconditionally; a field that cannot be resolved is left alone rather than
 * guessed at, which keeps a TEXT answer that happens to contain ";" intact.
 */
export function FormatCustomFieldAnswerForDisplay(
  value: string,
  field: { type?: CustomFieldType; render_separator?: RenderSeparator | null } | null | undefined
): string {
  if (field?.type !== CustomFieldType.MULTI_SELECT) {
    return value;
  }
  return ParseMultiSelectValue(value).join(renderSeparatorString(field.render_separator));
}

/**
 * Rewrites every MULTI_SELECT entry of a submitted custom-field list into its wire form, leaving
 * every other type untouched. Call this on the payload object (a fresh `form.value`), never on the
 * form itself — the control must keep holding an array so the checkbox group stays bound to it.
 */
export function SerializeCustomFieldValuesForWire<T extends { type?: CustomFieldType; value?: any }>(
  fields: T[] | undefined | null
): T[] {
  return (fields ?? []).map(field =>
    field?.type === CustomFieldType.MULTI_SELECT
      ? { ...field, value: SerializeMultiSelectValue(field.value) }
      : field
  );
}

export interface DateDefaultSettings {
  default_optional_dates_to_today: boolean;
  default_required_dates_to_today: boolean;
}

export function GenerateCustomFieldForm(
  fb: FormBuilder,
  customField: CustomField = {} as CustomField,
  dateDefaults?: DateDefaultSettings
): CustomFieldForm {
  let value;
  let required = customField.required;
  switch (customField.type) {
    case CustomFieldType.BOOLEAN:
      value = false;
      break;
    case CustomFieldType.DATE:
      const shouldDefaultToToday = customField.required
        ? (dateDefaults?.default_required_dates_to_today ?? true)
        : (dateDefaults?.default_optional_dates_to_today ?? true);
      value = shouldDefaultToToday ? new Date() : null;
      break;
    case CustomFieldType.SELECT:
      required = required && customField.options && customField.options.length > 0;
      // No `value` assignment on purpose, and it is not an oversight: FormBuilder normalises an
      // undefined initial state to null, so the control starts at null exactly like every other
      // scalar type and `form.value` still carries the key. Pinned by a test — see
      // custom-field.form.multi-select.spec.ts — because it reads like a missing case otherwise.
      break;
    case CustomFieldType.MULTI_SELECT:
      // Gated on the options the renderer will actually DRAW, not on `options` alone. The checkbox
      // group is seeded from multiSelectSeedOptions, which prefers `option_definitions` — so a field
      // carrying definitions but no `options` would render its choices and silently lose
      // Validators.required, letting a required group submit empty (Req 5.3).
      required = required && multiSelectSeedOptions(customField).length > 0;
      // A MULTI_SELECT control holds an array from the very first render, so an untouched field
      // submits [] -> '' rather than undefined. A stored answer arrives as the ';'-joined wire
      // string (CustomFieldDTO.value is a String), so parse rather than assign.
      value = ParseMultiSelectValue(customField.value);
      break;
    default:
      value = null;
  }

  // MULTI_SELECT only, and ADDED rather than left empty, for the same reason as the builder form
  // below: these keys must not appear on a field of any other type (Req 1.4). The render form needs
  // them because `ee-custom-field` captions its checkboxes from the definitions — without them it
  // could only show raw stored values.
  const multiSelectControls = customField.type === CustomFieldType.MULTI_SELECT
    ? {
      option_definitions: fb.array(
        multiSelectSeedOptions(customField).map(option => GenerateCustomFieldOptionForm(fb, option))
      ),
      render_separator: [customField.render_separator ?? RenderSeparator.COMMA]
    }
    : {};

  // Cast for the same reason GenerateCustomFieldBuilderForm casts: the spread of the conditional
  // MULTI_SELECT controls makes the inferred group a union, which no longer matches CustomFieldForm's
  // optional FormArray member.
  return fb.group({
    order: customField.order,
    columns: customField.columns,
    label: customField.label,
    hint: customField.hint,
    hint_more_info: customField.hint_more_info,
    field: customField.field,
    type: customField.type,
    required: required,
    link_id: customField.link_id,
    false_label: customField.false_label,
    true_label: customField.true_label,
    include_in_total: customField.include_in_total,
    monetary: customField.monetary ?? (customField.type === CustomFieldType.DECIMAL),
    hide: customField.hide,
    hide_from_intake: customField.hide_from_intake,
    built_in_field: customField.built_in_field || CUSTOM_FIELDS.some((f) => f.field === customField.field),
    value: [value, required && customField.type !== CustomFieldType.BOOLEAN ? [Validators.required] : []],
    options: [customField.options],
    ...multiSelectControls,
    prevent_removal: customField.prevent_removal ?? false,
    description: customField.description,
    example: customField.example
  }) as CustomFieldForm;
}

export function GenerateCustomFieldBuilderForm(
  fb: FormBuilder,
  values?: CustomField,
  reservedFieldNames: string[] = [],
  equationEligibleFields: string[] | null = []
): CustomFieldForm {
  const isNew = !values?.field;
  const isDocument = values?.type === CustomFieldType.FILE_UPLOAD;
  // FILE_UPLOAD "field" is an AttachmentType enum key chosen from a dropdown (UPPER_SNAKE_CASE),
  // not a hand-typed custom-field code, so the c_-prefix rule must not apply to it.
  const fieldPattern = isDocument ? ANY_SNAKE_CASE : isNew ? CUSTOM_FIELD_CODE : ANY_SNAKE_CASE;
  const fieldValidators = [Validators.required, Validators.pattern(fieldPattern)];
  if (reservedFieldNames.length) {
    fieldValidators.push(AvailableFieldValidator(reservedFieldNames));
  }

  const fieldControl = new FormControl(values?.field ?? undefined, fieldValidators);

  const optionsValidation =
    values?.type === CustomFieldType.SELECT ? [Validators.required, Validators.minLength(1)] : [];

  // The option-definition rows are only meaningful for MULTI_SELECT; every other type keeps
  // label-equals-value (Req 2.6). Both controls are ADDED ONLY for MULTI_SELECT, not merely left
  // empty — the dialogs close with getRawValue() and the PM builder and the attorney intake
  // customizer write that over every field of every section, so an unconditional control would
  // start persisting `option_definitions: []` / `render_separator: null` onto every existing field
  // of every type, which Req 1.4 forbids.
  //
  // No validator is attached here. A blocking rule belongs only where the list can actually be
  // fixed: the intake-form-builder dialog shows these options read-only, disables CLOSE on invalid
  // and offers CANCEL only in add mode, so a validator there would trap an admin in an
  // undismissable dialog over a list they cannot edit. The editable editor component attaches it.
  const isMultiSelect = values?.type === CustomFieldType.MULTI_SELECT;
  // With no definitions, each `options` entry is an option whose label equals its value (Req 2.2).
  // Without this a MULTI_SELECT derived from a legacy SurveyJS checkbox question would open with an
  // empty option list and no way to tell that from a genuinely new field.
  const multiSelectControls = isMultiSelect
    ? {
      option_definitions: fb.array(
        multiSelectSeedOptions(values).map(option => GenerateCustomFieldOptionForm(fb, option))
      ),
      render_separator: [values?.render_separator ?? RenderSeparator.COMMA]
    }
    : {};

  // convert the following to FormControl syntax. The cast reconciles the option-definition array,
  // which only this (field-definition) form carries — the render form has no options editor.
  const form = fb.group({
    value: [{ value: undefined, disabled: false }], // only here so custom field component doesn't blow up
    field: fieldControl,
    label: [values?.label ?? '', [Validators.required]],
    hint: values?.hint ?? undefined,
    hint_more_info: values?.hint_more_info ?? undefined,
    order: [values?.order ?? undefined],
    columns: [values?.columns ?? 4, [Validators.required]],
    type: [values?.type ?? undefined, [Validators.required]],
    link_id: values?.link_id ?? undefined,
    required: values?.required ?? false,
    options: [values?.options ?? [], optionsValidation],
    ...multiSelectControls,
    false_label: [values?.false_label ?? undefined],
    true_label: [values?.true_label ?? undefined],
    include_in_total: [values?.include_in_total ?? (values?.type === CustomFieldType.RENT_HISTORY)],
    monetary: [values?.monetary ?? (values?.type === CustomFieldType.DECIMAL)],
    hide: [values?.hide ?? false],
    hide_from_intake: [values?.hide_from_intake ?? false],
    prevent_removal: [values?.prevent_removal ?? false],
    built_in_field: [values?.built_in_field || CUSTOM_FIELDS.some((f) => f.field === values?.field)],
    description: [values?.description ?? undefined],
    example: [values?.example ?? undefined]
  }) as CustomFieldForm;

  if (values?.type === CustomFieldType.CALCULATED) {
    form.get('hint')?.disable();
    form.get('hint_more_info')?.disable();
    form.get('value')?.enable();
    if (values?.value) {
      form.get('value').setValue(values.value);
    }
    // Calculated fields are computed, never collected on intake: always hidden there and
    // never required. The editor dialog hides both toggles for this type.
    form.get('hide_from_intake').setValue(true);
    form.get('required').setValue(false);
    // Only validate the equation in contexts that actually edit it (they pass the eligible
    // fields, e.g. the Field Settings editor). Callers like the intake form customizer pass
    // no/empty eligible fields and don't render the equation — attaching the validators there
    // makes the form group permanently invalid and soft-locks dialogs gated on validity.
    if (equationEligibleFields?.length) {
      form.get('value').addValidators([Validators.required, ValidateQuillEquation(equationEligibleFields)]);
    }
  }

  return form;
}
